import { h, defineComponent, ref, watch } from 'vue';
import { box, column, indexView, loader, pagination, textColumn } from '@/ui/components';
import { filterFormLoader } from '@/ui/components/Loader';
import {
  assigneeFilter,
  searchFilter,
  entityItemFilter,
  dateRangeFilter,
} from '@/ui/components/filters/filters-horizontal';
import { list as getUpcomingForms, remove, bulkDelete } from '@/services/api/resources/upcoming';
import { UpcomingList, UpcomingRecord } from '@/entities/upcoming';
import { d, t } from '@/ui/i18n/i18n';
import { tagColumn } from '@/ui/components/table/TableColumn';
import { dataNameProp, dataTitleProp, filterListToOptions, sequence } from '@/ui/helpers';
import { SelectOptions, SortDirection } from '@/ui/types';
import * as qs from '@/ui/queryState';
import { usePagination, useSorting, useTask } from '@/ui/concerns';
import { filter as getAllUsers } from '@/services/api/resources/user';
import { filter as getAllEntityItems } from '@/services/api/resources/entityItem';
import { emptyList } from '@/services/api';
import { router } from '@/ui/routes';
import { useUpcomingForms } from '@/ui/pages/inspection/useUpcomingForms';
import InspectionUpcomingEmpty from '@/ui/pages/inspection/InspectionUpcomingEmpty.vue';
import { Button, bulkDeleteButton } from '@/ui/components/Button';
import { useBulkDeletable } from '@/ui/pages/useBulkDeletable';
import { BulkDeletableTable } from '@/ui/components/table/BulkDeletableTable';
import { Icon } from '@/ui/components/icon/Icon';
import { user } from '@/ui/auth/auth';
import { CLICK_EVENTS, trackClick } from '@/services/activityTracker/activityTrackerHelper';
import { FormsTabNames } from '@/ui/components/navigation/constants';

export const UpcomingFormsTab = defineComponent({
  name: 'UpcomingFormsTab',
  setup() {
    const initialLoading = ref(true);

    // state stored in query string
    const { page, perPage, resetPagination } = usePagination();
    const { sort, dir, onSortChange } = useSorting('availableAtStart', SortDirection.Ascending);
    const { checkResult, performCheck } = useUpcomingForms();

    const search = qs.string('q', '');
    const assignee = qs.numbers('assignee', []);
    const entityItem = qs.numbers('entityItem', []);
    const availableAtStart = qs.nullableDate('availableAtStart', null);
    const availableAtEnd = qs.nullableDate('availableAtEnd', null);
    const dueAtStart = qs.nullableDate('dueAtStart', null);
    const dueAtEnd = qs.nullableDate('dueAtEnd', null);

    // get filters task
    const users = ref<SelectOptions>([]);
    const entityItems = ref<SelectOptions>([]);
    const getFilters = useTask(
      () => sequence(getAllUsers(), getAllEntityItems()),
      ([userList, entityItemList]) => {
        users.value = filterListToOptions(userList);
        entityItems.value = filterListToOptions(entityItemList);
      },
    );

    // get list task
    const list = ref<UpcomingList>(emptyList());
    const getList = useTask(
      () => getUpcomingForms({
        timezone: user.value!.timezone,
        page: page.value,
        perPage: perPage.value,
        search: search.value,
        assignee: assignee.value,
        entityItem: entityItem.value,
        availableAtStart: availableAtStart.value,
        availableAtEnd: availableAtEnd.value,
        dueAtStart: dueAtStart.value,
        dueAtEnd: dueAtEnd.value,
        sort: sort.value,
        dir: dir.value,
      }),
      listResponse => {
        list.value = listResponse;
      },
    );

    watch([
      page,
      perPage,
      search,
      assignee,
      entityItem,
      availableAtStart,
      availableAtEnd,
      dueAtStart,
      dueAtEnd,
      sort,
      dir,
    ], getList.run);

    watch(() => getList.state.value.pending, newVal => {
      initialLoading.value = newVal;
    });

    const {
      selectedResources,
      deleteBulkTask,
      onCheckedRowsUpdate,
      isBulkDeletionDisabled,
    } = useBulkDeletable<UpcomingRecord>({
      getList,
      bulkDeleteFn: bulkDelete,
      mapIds: record => record.id,
      // eslint-disable-next-line max-len
      onDeleteFilterFn: inspection => (currentElement => currentElement.id !== inspection.id),
    });

    return function render() {
      return loader('div', performCheck, [
        checkResult.value ? indexView(
          [
            filterFormLoader(getFilters, [
              bulkDeleteButton({ resources: selectedResources, disabled: isBulkDeletionDisabled }),
              searchFilter(search, resetPagination),
              dateRangeFilter(availableAtStart, availableAtEnd, resetPagination, 12, t('common.availableAt'), true),
              dateRangeFilter(dueAtStart, dueAtEnd, resetPagination, 12, t('common.deadline'), true),
              assigneeFilter(assignee, users.value, resetPagination),
              entityItemFilter(entityItem, entityItems.value, resetPagination),
            ]),
          ],
          [
            box([
              loader('div', getList, [
                h(BulkDeletableTable, {
                  props: {
                    list: list.value.data,
                    deleteFn: remove,
                    loading: deleteBulkTask.state.value.pending,
                    isHoverable: false,
                    selectedRecords: selectedResources.value,
                    paginationLinks: list.value.meta,
                    isRowCheckable: (row: BaseEntity) => row.authorizedTo.delete,
                    recordName: {
                      plural: t('inspections.scheduled'),
                      singular: t('common.scheduled') + t('common.inspection'),
                    },
                  },
                  scopedSlots: {
                    default: () => [
                      column(
                        {
                          field: 'title',
                          label: t('common.title'),
                        }, {
                          default: ({ row: inspection }: { row: UpcomingRecord }) => inspection.data.title,
                        },
                      ),
                      textColumn(
                        {
                          field: 'availableAt',
                          label: t('common.availableAt'),
                          cellClass: 'availableAt-column',
                        }, {
                          default: ({ row: inspection }: { row: UpcomingRecord }) => h('span', d(inspection.data.availableAt)),
                        },
                      ),
                      textColumn(
                        {
                          field: 'dueAt',
                          label: t('common.deadline'),
                          cellClass: 'deadline-column',
                        }, {
                          default: ({ row: inspection }: { row: UpcomingRecord }) => h('span', inspection.data.dueAt ? d(inspection.data.dueAt) : null),
                        },
                      ),
                      tagColumn({
                        field: 'assignees',
                        label: t('inspection-report.assignee'),
                      }, {
                        default: ({ row: inspection }: { row: UpcomingRecord }) =>
                          h('div', inspection.data.assignees.map(dataNameProp).join(', ')),
                      }),
                      textColumn(
                        {
                          field: 'entityItems',
                          label: t('common.entity-item'),
                        }, {
                          default: ({ row: inspection }: { row: UpcomingRecord }) =>
                            inspection.data.allEntityItems
                              ? t('template-builder.schedule.all-entity-items')
                              : inspection.data.entityItems.map(dataTitleProp).join(', '),
                        },
                      ),
                    ],

                    beforeDeleteButton: ({ row: inspection }: { row: UpcomingRecord }) =>
                      inspection.authorizedTo.view ? h(Button, {
                        props: { type: 'is-light' },
                        class: 'mr-1',
                        on: {
                          click: () => {
                            trackClick(CLICK_EVENTS.EDIT_TEMPLATE, { tab: FormsTabNames.UPCOMING });

                            router.push({
                              name: 'checklistEdit',
                              params: { id: `${inspection.data.checklistId}` },
                            });
                          },
                        },
                      }, [
                        h('span', { class: 'icon is-small' }, [
                          h(Icon, { props: { icon: 'edit' } }),
                        ]),
                      ]) : null,
                  },
                  on: {
                    sort: (field: string, direction: SortDirection) => qs.updateMultiple(() => {
                      onSortChange(field, direction);
                      resetPagination();
                    }),
                    'update:checkedRows': onCheckedRowsUpdate,
                  },
                }),
                pagination({
                  current: page.value,
                  total: list.value.meta.total,
                  perPage: perPage.value,
                }),
              ], !initialLoading.value),
            ]),
          ],
        ) : box([h(InspectionUpcomingEmpty)]),
      ], false);
    };
  },
});
